import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/lib/i18n";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import Studio from "@/pages/Studio";
import Operations from "@/pages/Operations";
import Admin from "@/pages/Admin";
import Engines from "@/pages/Engines";
import CostGovernor from "@/pages/CostGovernor";
import VoiceDubbing from "@/pages/VoiceDubbing";
import Distribution from "@/pages/Distribution";
import Security from "@/pages/Security";
import Placeholder from "@/pages/Placeholder";
import PublicLayout from "@/components/PublicLayout";
import Marketing from "@/pages/Marketing";
import Billing from "@/pages/Billing";
import Account from "@/pages/Account";
import Faceless from "@/pages/Faceless";
import Agent from "@/pages/Agent";
import Assets from "@/pages/Assets";
import Avatars from "@/pages/Avatars";
import Auth from "@/pages/Auth";
import Verify from "@/pages/Verify";
import ResetPassword from "@/pages/ResetPassword";
import NotFound from "@/pages/NotFound";
import Renders from "@/pages/Renders";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07080E]">
        <div className="text-slate-400 font-mono text-sm animate-pulse">Initializing Vectra OS...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
};

const PUBLIC = [
  ["/about", "/solutions", "/enterprise", "/pricing", "/how-it-works", "/cinematic-arsenal", "/privacy", "/terms", "/refunds", "/contact"],
];

function AppRouter() {
  const location = useLocation();
  // read hash from useLocation (reactive), not window.location.hash
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/hi" element={<Login />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify" element={<ProtectedRoute><Verify /></ProtectedRoute>} />
      <Route element={<PublicLayout />}>
        {PUBLIC[0].map((p) => <Route key={p} path={p} element={<Marketing />} />)}
        {PUBLIC[0].map((p) => <Route key={`hi${p}`} path={`/hi${p}`} element={<Marketing />} />)}
      </Route>
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/studio" element={<Studio />} />
        <Route path="/operations" element={<Operations />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/engines" element={<Engines />} />
        <Route path="/cost-governor" element={<CostGovernor />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/account" element={<Account />} />
        <Route path="/faceless" element={<Faceless />} />
        <Route path="/agent" element={<Agent />} />
        <Route path="/assets" element={<Assets />} />
        <Route path="/voice-dubbing" element={<VoiceDubbing />} />
        <Route path="/avatars" element={<Avatars />} />
        <Route path="/renders" element={<Renders />} />
        <Route path="/workflows" element={<Placeholder pageKey="workflows" />} />
        <Route path="/quality" element={<Placeholder pageKey="quality" />} />
        <Route path="/distribution" element={<Distribution />} />
        <Route path="/security" element={<Security />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LangProvider>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="top-right" richColors />
      </AuthProvider>
      </LangProvider>
    </BrowserRouter>
  );
}

export default App;
